import { useCallback, useEffect, useRef, useState } from 'react';

export function useCallAlerts(callState, name, openChat, enabled) {
  const audioRef = useRef(null);
  const [permission, setPermission] = useState(() => typeof Notification === 'undefined' ? 'unsupported' : Notification.permission);
  const [soundReady, setSoundReady] = useState(false);
  const unlock = useCallback(() => {
    const Audio = window.AudioContext || window.webkitAudioContext;
    if (!Audio) return;
    try { audioRef.current ||= new Audio(); audioRef.current.resume().then(() => setSoundReady(true)).catch(() => {}); } catch { /* Browser requires a user gesture. */ }
  }, []);
  useEffect(() => {
    if (!enabled) return undefined;
    document.addEventListener('pointerdown', unlock, { once: true });
    document.addEventListener('keydown', unlock, { once: true });
    return () => { document.removeEventListener('pointerdown', unlock); document.removeEventListener('keydown', unlock); };
  }, [enabled, unlock]);
  const requestAlerts = useCallback(async () => {
    unlock();
    if (typeof Notification === 'undefined') return 'unsupported';
    const result = await Notification.requestPermission(); setPermission(result); return result;
  }, [unlock]);
  const ringing = enabled && callState.call?.status === 'ringing';
  useEffect(() => {
    if (!ringing) return undefined;
    let nodes = [];
    const pulse = () => {
      const audio = audioRef.current;
      if (!audio || audio.state !== 'running') return;
      const tone = audio.createOscillator(); const gain = audio.createGain();
      tone.frequency.value = callState.call.direction === 'incoming' ? 660 : 440;
      gain.gain.setValueAtTime(0.04, audio.currentTime); gain.gain.exponentialRampToValueAtTime(0.001, audio.currentTime + 0.8);
      tone.connect(gain).connect(audio.destination); tone.start(); tone.stop(audio.currentTime + 0.85);
      nodes.push(tone); tone.onended = () => { nodes = nodes.filter((node) => node !== tone); tone.disconnect(); gain.disconnect(); };
    };
    pulse(); const timer = setInterval(pulse, 2500);
    return () => { clearInterval(timer); nodes.forEach((node) => { try { node.stop(); } catch {} }); };
  }, [ringing, callState.call?.direction, soundReady]);
  useEffect(() => {
    if (!ringing || callState.call.direction !== 'incoming' || permission !== 'granted' || document.visibilityState !== 'hidden') return undefined;
    let notice; let cancelled = false;
    const title = `Incoming ${callState.call.media_type} call`;
    const options = { body: `${name} is calling`, tag: `call-${callState.call.call_id}`, data: { kind: 'call', call_id: callState.call.call_id, url: `/${localStorage.getItem('proovam_user_type') || 'client'}/messages?conversation_id=${encodeURIComponent(callState.call.conversation_id)}&call_id=${encodeURIComponent(callState.call.call_id)}` } };
    (async () => {
      const registration = await navigator.serviceWorker?.getRegistration?.();
      if (cancelled) return;
      if (registration?.showNotification) await registration.showNotification(title, options);
      else { notice = new Notification(title, options); notice.onclick = () => { window.focus(); openChat(); notice.close(); }; }
    })().catch(() => {});
    return () => { cancelled = true; notice?.close(); navigator.serviceWorker?.getRegistration?.().then((registration) => registration?.getNotifications?.({ tag: options.tag })).then((items) => items?.forEach((item) => item.close())).catch(() => {}); };
  }, [ringing, callState.call?.call_id, callState.call?.direction, callState.call?.media_type, callState.call?.conversation_id, name, permission, openChat]);
  useEffect(() => () => { audioRef.current?.close().catch(() => {}); }, []);
  return { permission, soundReady, requestAlerts };
}
